import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type Column } from '@/components/DataTable'
import { PromotionPanel } from '@/features/analytics/insights'
import { useMayOpen } from '@/features/auth/access'
import {
  formatMoney,
  formatPct,
  getAffinity,
  getProducts,
  type AffinityRule,
  type ProductRow,
} from '@/features/analytics/api'
import {
  CategoryMix,
  ChartCard,
  Kpi,
  PanelCard,
  ProductBars,
  WindowPicker,
} from '@/features/analytics/panels'

/**
 * Product velocity, the two ends of the range, and what sells with what.
 *
 * Top and bottom are the same query read from both ends, and both are ranked on revenue
 * with velocity beside it. Revenue alone promotes a slow expensive line over a fast cheap
 * one; velocity alone does the reverse. A buyer needs to see the pair to act on either.
 *
 * Affinity is counted over till baskets only. An order line is placed by a rep against a
 * customer over a phone call, which says something about the rep's habits and nothing about
 * what a shopper reaches for at the same time -- the till is the only place the question is
 * actually being asked. Lift above 1 means the pair beats chance; below it, the two are
 * bought together less often than their own popularity would predict.
 */
export function ProductAnalytics() {
  const [days, setDays] = useState(30)

  const products = useQuery({
    queryKey: ['analytics', 'products', days, 20],
    queryFn: () => getProducts({ days, limit: 20 }),
    retry: false,
  })
  const affinity = useQuery({
    queryKey: ['analytics', 'affinity', days],
    queryFn: () => getAffinity({ days, limit: 12 }),
    retry: false,
  })

  const mayOpenProduct = useMayOpen('/products/:productId')
  const nameOf = (row: ProductRow) =>
    row.product_code
      ? `${row.product_code} · ${row.product_name ?? ''}`.trim()
      : (row.product_name ?? row.product_id.slice(0, 8))

  const productLink = (row: ProductRow) =>
    mayOpenProduct ? (
      <Link to={`/products/${row.product_id}`} className="hover:underline">
        {nameOf(row)}
      </Link>
    ) : (
      nameOf(row)
    )

  const columns: Column<ProductRow>[] = [
    {
      key: 'product',
      header: 'Product',
      className: 'font-medium',
      sortValue: (row) => nameOf(row),
      cell: productLink,
    },
    {
      key: 'category',
      header: 'Category',
      className: 'text-slate-500 dark:text-slate-400',
      sortValue: (row) => row.category_name ?? '',
      cell: (row) => row.category_name ?? '—',
    },
    {
      key: 'units',
      header: 'Units',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.units,
      cell: (row) => row.units.toLocaleString(),
    },
    {
      key: 'velocity',
      header: 'Per day',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.velocity,
      cell: (row) => row.velocity.toFixed(2),
    },
    {
      key: 'net',
      header: 'Revenue',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.net,
      cell: (row) => formatMoney(row.net),
    },
    {
      key: 'margin',
      header: 'Margin',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.margin,
      cell: (row) => `${formatMoney(row.margin)} · ${formatPct(row.margin_pct)}`,
    },
  ]

  const affinityColumns: Column<AffinityRule>[] = [
    {
      key: 'pair',
      header: 'Bought together',
      className: 'font-medium',
      sortValue: (rule) => rule.antecedent_name ?? '',
      cell: (rule) => `${rule.antecedent_name ?? '—'} → ${rule.consequent_name ?? '—'}`,
    },
    {
      key: 'support',
      header: 'Baskets',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (rule) => rule.support,
      cell: (rule) => formatPct(rule.support * 100),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (rule) => rule.confidence,
      cell: (rule) => formatPct(rule.confidence * 100),
    },
    {
      key: 'lift',
      header: 'Lift',
      className: 'text-right font-medium tabular-nums',
      headerClassName: 'text-right',
      sortValue: (rule) => rule.lift,
      cell: (rule) => `${rule.lift.toFixed(2)}×`,
    },
  ]

  const top = products.data?.top ?? []
  const bottom = products.data?.bottom ?? []

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Product analytics</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            What is moving, what is not, and what sells alongside it.
          </p>
        </div>
        <WindowPicker days={days} onChange={setDays} asOf={products.data?.as_of} />
      </header>

      <div className="grid gap-4 tablet:grid-cols-3">
        {/* Not "products sold": the API returns the two ends of the range, not the whole
            catalog, so the honest headline is how many lines are ranked below. */}
        <Kpi
          label="Products ranked"
          value={products.data ? String(top.length + bottom.length) : '—'}
          hint={`${top.length} fastest · ${bottom.length} slowest`}
          loading={products.isLoading}
        />
        <Kpi
          label="Fastest mover"
          value={top[0] ? `${top[0].velocity.toFixed(1)}/day` : '—'}
          hint={top[0] ? (top[0].product_name ?? '') : 'Nothing sold'}
          loading={products.isLoading}
        />
        <Kpi
          label="Baskets analysed"
          value={affinity.data ? affinity.data.baskets.toLocaleString() : '—'}
          hint={`${affinity.data?.rules.length ?? 0} pairs beat chance`}
          loading={affinity.isLoading}
        />
      </div>

      <div className="grid gap-4 desktop:grid-cols-2">
        <ChartCard
          title="Top sellers"
          description={`By revenue over ${days} days.`}
          isLoading={products.isLoading}
          error={products.error}
          isEmpty={!top.length}
          emptyMessage="No product sold in this window."
        >
          <ProductBars rows={top.slice(0, 8)} />
        </ChartCard>

        <ChartCard
          title="Revenue by category"
          isLoading={products.isLoading}
          error={products.error}
          isEmpty={!products.data?.categories.length}
          emptyMessage="No category sold in this window."
        >
          <CategoryMix categories={products.data?.categories ?? []} />
        </ChartCard>
      </div>

      <DataTable
        rows={top}
        columns={columns}
        getRowId={(row) => row.product_id}
        caption="Best selling products by revenue"
        isLoading={products.isLoading}
        error={products.error}
        emptyMessage="No product sold in this window."
        search={{
          placeholder: 'Search products',
          matches: (row, query) => nameOf(row).toLowerCase().includes(query),
        }}
      />

      <PanelCard
        title="Slowest movers"
        description="Sold at least once, and least of all. Products that never sold are not here — the rollup only knows what moved."
        isLoading={products.isLoading}
        error={products.error}
        isEmpty={!bottom.length}
        emptyMessage="No product sold in this window."
      >
        <DataTable
          rows={bottom}
          columns={columns}
          getRowId={(row) => row.product_id}
          caption="Slowest selling products by revenue"
          emptyMessage="No product sold in this window."
        />
      </PanelCard>

      <PanelCard
        title="Bought together"
        description="Till baskets only. Lift above 1 means the pair beats chance."
        isLoading={affinity.isLoading}
        error={affinity.error}
        isEmpty={!affinity.data?.rules.length}
        emptyMessage="No pair of products was bought together often enough to mean anything."
      >
        <DataTable
          rows={affinity.data?.rules ?? []}
          columns={affinityColumns}
          getRowId={(rule) => `${rule.antecedent_id}:${rule.consequent_id}`}
          caption="Product pairs bought together, by lift"
          emptyMessage="No pair of products was bought together often enough to mean anything."
        />
      </PanelCard>

      <PromotionPanel days={Math.max(days, 90)} />
    </div>
  )
}
